import { errorAlert } from '@xdocs/schemas';
import type { ErrorAlert, WsOp } from '@xdocs/schemas';
import { buildReqEnvelope, decodeEnvelope, encodeEnvelope, generateId } from '@xdocs/ws-protocol';

const DEFAULT_TIMEOUT_MS = 30_000;

function transportAlert(messageKey: string): ErrorAlert {
  return { code: 'INTERNAL', message: messageKey, fields: [] };
}

export class AuthTransportError extends Error {
  readonly alert: ErrorAlert;

  constructor(alert: ErrorAlert) {
    super(alert.message);
    this.name = 'AuthTransportError';
    this.alert = alert;
  }
}

/**
 * One-shot pre-handshake exchange for the `auth.*` ops that must work
 * BEFORE the client holds an access token (`auth.login`, `auth.signup`,
 * `auth.magicLink.*`, `auth.passwordReset.*`).
 *
 * Deliberately bypasses `WsClient` (`@xdocs/ws-protocol`): that class's
 * `connect()` always runs `auth.handshake` first and only flips to
 * `'connected'` — the only state `request()` will send from — once
 * handshake succeeds, which requires an access token we don't have yet.
 * Per CODE_GUIDELINES §5.4 op-handler contract ("(1) require auth *unless
 * an `auth.*` op*"), the server accepts these ops pre-handshake on a raw
 * socket, so this opens a fresh one, sends exactly one `req` frame, resolves
 * on the matching `res`/rejects on `err`/timeout/close, and closes the
 * socket — no reconnect/heartbeat/resilience machinery needed for a
 * single request.
 */
export function requestAuthOp<TResult>(
  wsUrl: string,
  op: WsOp,
  payload: unknown,
  opts?: { timeoutMs?: number },
): Promise<TResult> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const id = generateId();

    let socket: WebSocket;
    try {
      socket = new WebSocket(wsUrl);
    } catch {
      reject(new AuthTransportError(transportAlert('errors:code.INTERNAL')));
      return;
    }

    function settle(fn: () => void): void {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        // already closed/closing
      }
      fn();
    }

    timer = setTimeout(() => {
      settle(() => reject(new AuthTransportError(transportAlert('errors:ws.request_timeout'))));
    }, opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS);

    socket.onopen = () => {
      try {
        socket.send(encodeEnvelope(buildReqEnvelope({ id, op, payload, ts: Date.now() })));
      } catch {
        settle(() => reject(new AuthTransportError(transportAlert('errors:code.INTERNAL'))));
      }
    };

    socket.onmessage = (event: MessageEvent<unknown>) => {
      if (typeof event.data !== 'string') return;
      const decoded = decodeEnvelope(event.data);
      if (!decoded.ok || decoded.envelope.id !== id) return;
      const envelope = decoded.envelope;
      if (envelope.type === 'res') {
        settle(() => resolve(envelope.payload as TResult));
      } else if (envelope.type === 'err') {
        const parsed = errorAlert.safeParse(envelope.payload);
        settle(() =>
          reject(new AuthTransportError(parsed.success ? parsed.data : transportAlert('errors:code.INTERNAL'))),
        );
      }
    };

    socket.onerror = () => {
      settle(() => reject(new AuthTransportError(transportAlert('errors:code.INTERNAL'))));
    };

    socket.onclose = () => {
      settle(() => reject(new AuthTransportError(transportAlert('errors:ws.disconnected'))));
    };
  });
}
